const SVG_CACHE_NAME = 'svg-content-cache';
const SVG_CACHE_EXPIRATION_MS = 30 * 24 * 60 * 60 * 1000;
// Skip background revalidation if the cached entry is younger than this — keeps in-session navigation network-free.
const SVG_CACHE_FRESH_MS = 10 * 60 * 1000;

type SvgEntry = {
    text: string;
    storedAt: number;
};

export type SvgFetchResult = {
    text: string | null;
    /** Background revalidation. Resolves to fresh text only if the server returned a changed body, otherwise null. */
    revalidation?: Promise<string | null>;
};

const inFlight = new Map<string, Promise<unknown>>();

const dedup = <T>(key: string, factory: () => Promise<T>): Promise<T> => {
    const existing = inFlight.get(key) as Promise<T> | undefined;
    if (existing) return existing;
    const promise = factory().finally(() => inFlight.delete(key));
    inFlight.set(key, promise);
    return promise;
};

const readEntry = async (response: Response): Promise<SvgEntry> => ({
    text: await response.text(),
    storedAt: Number(response.headers.get('x-stored-time') ?? 0),
});

const entryToResponse = (entry: SvgEntry): Response =>
    new Response(entry.text, {
        headers: {
            'Content-Type': 'image/svg+xml',
            'x-stored-time': entry.storedAt.toString(),
        },
    });

const fetchFromNetwork = async (src: string): Promise<SvgEntry> => {
    const response = await fetch(src, {
        method: 'GET',
        headers: new Headers({ accept: 'image/svg+xml,image/*,*/*;q=0.8' }),
    });
    if (!response.ok) {
        throw new Error(`Failed to fetch SVG: ${response.status} ${response.statusText}`);
    }
    return { text: await response.text(), storedAt: Date.now() };
};

export const fetchAndCacheSvg = async (src: string): Promise<SvgFetchResult> => {
    if (!('caches' in globalThis)) {
        try {
            const entry = await fetchFromNetwork(src);
            return { text: entry.text };
        } catch (error) {
            console.error('Error fetching SVG without cache:', error);
            return { text: null };
        }
    }

    try {
        const cache = await caches.open(SVG_CACHE_NAME);
        const cachedResponse = await cache.match(src);

        if (cachedResponse) {
            const entry = await readEntry(cachedResponse);
            const age = Date.now() - entry.storedAt;

            if (age > SVG_CACHE_EXPIRATION_MS) {
                await cache.delete(src);
            } else if (age < SVG_CACHE_FRESH_MS) {
                return { text: entry.text };
            } else {
                // Stale-while-revalidate: render cached text now, refetch in the background and update if body changed.
                const revalidation = dedup(`revalidate:${src}`, async () => {
                    try {
                        const fresh = await fetchFromNetwork(src);
                        await cache.put(src, entryToResponse(fresh));
                        return fresh.text === entry.text ? null : fresh.text;
                    } catch (error) {
                        console.error('Error revalidating SVG:', error);
                        return null;
                    }
                });
                return { text: entry.text, revalidation };
            }
        }

        const text = await dedup(`fetch:${src}`, async () => {
            const entry = await fetchFromNetwork(src);
            await cache.put(src, entryToResponse(entry));
            return entry.text;
        });
        return { text };
    } catch (error) {
        console.error('Error fetching and caching SVG:', error);
        return { text: null };
    }
};
